import React, { Suspense } from "react";

import Box from "@/ui/atoms/Box";
import Loader from "@/ui/atoms/Loader";
import GridLoader from "@/ui/molecules/GridLoader";

import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";

import type {
    GridSynchronousDoNotUseDirectlyAsItHasHugeImpactOnBundleSizeProps,
    GridSynchronousDoNotUseDirectlyAsItHasHugeImpactOnBundleSizeComponentType,
    TGridWrapperDefaultStyles,
    AgGridReact,
} from "./GridSynchronousDoNotUseDirectlyAsItHasHugeImpactOnBundleSize";

const createGridSynchronousDoNotUseDirectlyAsItHasHugeImpactOnBundleSize = (
    delay: number | "forever" = 0,
) =>
    React.lazy(async () => {
        if (delay === "forever") {
            const waitForEver = async () => await new Promise(() => {});
            await waitForEver();
        } else {
            const waitDelayTime = async () =>
                await new Promise(resolve => setTimeout(resolve, delay));
            await waitDelayTime();
        }

        return import(
            "./GridSynchronousDoNotUseDirectlyAsItHasHugeImpactOnBundleSize"
        );
    }) as GridSynchronousDoNotUseDirectlyAsItHasHugeImpactOnBundleSizeComponentType;

const gridWrapperDefaultStyles = {
    height: "100%",
    width: "100%",
} as const satisfies TGridWrapperDefaultStyles;

export const GridLoaderSpinner = <TRowData,>(props: GridProps<TRowData>) => (
    <Box
        sx={{
            ...gridWrapperDefaultStyles,
            ...props.sx,
        }}
    >
        <LayoutFillParentCentered>
            <Loader label="Grid is loading..." />
        </LayoutFillParentCentered>
    </Box>
);

export const GridLoaderSkeleton = <TRowData,>(props: GridProps<TRowData>) => (
    <Box
        sx={{
            ...gridWrapperDefaultStyles,
            ...props.sx,
        }}
    >
        <LayoutFillParentCentered>
            <GridLoader />
        </LayoutFillParentCentered>
    </Box>
);

export type GridProps<TRowData> =
    GridSynchronousDoNotUseDirectlyAsItHasHugeImpactOnBundleSizeProps<TRowData> & {
        gridSuspenseFallback?: React.ReactNode;
        gridLoaderComponent?: <TRowData>(
            props: GridProps<TRowData>,
        ) => React.ReactNode;
    };

export type GridPropsExtendedForStorybook<TRowData> = GridProps<TRowData> & {
    delayLazyLoadMs?: number | "forever";
};

export const testIds = {
    main: "Grid",
    container: "GridContainer",
};
const Grid = <TRowData,>(
    props: GridProps<TRowData>,
    ref: React.ForwardedRef<AgGridReact<TRowData>>,
) => {
    const {
        gridLoaderComponent: LoaderComponent = GridLoaderSpinner,
        gridSuspenseFallback,
        // @ts-expect-error this one is not typed on purpose as it is only used in Storybook to show the suspense state
        delayLazyLoadMs,
        ...gridProps
    } = props;

    const GridSynchronousDoNotUseDirectlyAsItHasHugeImpactOnBundleSize =
        React.useMemo(
            () =>
                createGridSynchronousDoNotUseDirectlyAsItHasHugeImpactOnBundleSize(
                    delayLazyLoadMs as number | "forever",
                ),
            [delayLazyLoadMs],
        );

    return (
        <Suspense
            fallback={
                gridSuspenseFallback ?? (
                    <LoaderComponent<TRowData> {...gridProps} />
                )
            }
        >
            <GridSynchronousDoNotUseDirectlyAsItHasHugeImpactOnBundleSize<TRowData>
                ref={ref}
                data-testid={testIds.main}
                containerProps={{
                    "data-testid": testIds.container,
                    ...gridProps.containerProps,
                }}
                {...gridProps}
            />
        </Suspense>
    );
};

Grid.displayName = "Grid";

type GridComponentType = <TRowData>(
    props: GridProps<TRowData> & {
        ref?: React.ForwardedRef<AgGridReact<TRowData>>;
    },
) => ReturnType<typeof Grid>;

export default React.forwardRef(Grid) as GridComponentType;

export type * from "./GridSynchronousDoNotUseDirectlyAsItHasHugeImpactOnBundleSize";
